import { I18N } from "oc-I18N";
import { filterPathIntersectionCached } from "oc-app/metadata/GraphMetadata";
import { cache, multiGroupBy } from "oc-commons/Commons";
import React, { useEffect } from "react";
import Select, { createFilter } from "react-select";
import { ZaunCircleMenuItem } from "./ZaunCircleMenuItem";
import { SvgButton } from "../../commons/SvgButton";
import { TrashBinSvg } from "../../inlinesvgs/TrashBinSvg";
import { MenuItemSmallButton } from "./MenuItemSmallButton";
import { MAuswahlwert } from "../../../metamodell";
import { PAuswahlwert } from "../../../metamodell_paths";
import { ZElement } from "../../arrangement/ZElement";

export function ZaunCircleMenuItemProfil() {
  const agt = APP.arrangement;

  const { Profil } = agt.auswahlAccessoren;
  const profil2profilekategorienAuswahlwertPath = (p: PAuswahlwert) =>
    p.Objekt.Profilkategorie.Auswahlwert;
  const profilkategorien = multiGroupBy(
    Profil.möglicheAuswahlwerte,
    (auswahlwert) =>
      filterPathIntersectionCached("ZaunCircleMenuItemProfil.jsx:18")(
        auswahlwert,
        profil2profilekategorienAuswahlwertPath
      ) as MAuswahlwert[]
  );
  const elemente = agt.selectedObjects.filter((e) => e?.isElement);

  if (profilkategorien.length == 0 || elemente.length == 0) {
    return null;
  }

  const element = agt.selectedObjects[0] as ZElement;
  if (!element?.sonderprofilMinMax.dekorprofilErlaubt) return null;

  const isRhombusOderRhombusHolz = elemente.some((e) => e.isRhombusOderRhombusHolz);
  const hatBasisprofile = isRhombusOderRhombusHolz && !(element.sonderprofilMinMax.mindesthöhe > 0);
  const selectedZaunelementOrPfosten = agt.selectedObjects[0];
  const isLichtleisteMöglich = [...profilkategorien.keys()].some((k) => k.Key.match(/licht/i));
  const buttonText = isLichtleisteMöglich
    ? I18N("Licht- oder Dekorprofil einfügen")
    : I18N("Dekorprofil einfügen");

  const removeProfile = () => {
    elemente.forEach((e) => {
      e.showProfil = null;
      e.profilKategorieAuswahlwert = null;
      e.profilAuswahlwert = null;
      e.profilPosition = null;
    });
  };

  const addDekorProfile = () => {
    if (!elemente.some((e) => e.showProfil)) {
      elemente.forEach((e) => e.setShowProfil(true));
      agt.activeTabKey = "Dekore";
      APP.dirty();
      APP.logUserAction({ action: "addDekorprofileButton" });
    }
  };

  const addLichtleiste = () => {
    if (!elemente.some((e) => e.showProfil)) {
      elemente.forEach((e) => {
        e.setShowProfil(true);
        e.addLichtleiste();
      });
      APP.dirty();
      APP.logUserAction({ action: "addLichtleisteButton" });
    }
  };

  const HöhenEingabe = () => (
    <>
      <React.Fragment>
        {I18N("Profil Position")}
        <PositionInput
          key={selectedZaunelementOrPfosten.id + ".y"}
          value={
            selectedZaunelementOrPfosten.profilPosition != null
              ? selectedZaunelementOrPfosten.profilPosition
              : selectedZaunelementOrPfosten.sonderprofilMinMax.maxPosition
          }
          setter={(value) =>
            elemente.forEach((e) => {
              e.profilPosition = value;
              e.profilPositionIndividualisiert = true;
              e.showIndividualisiert = true;
              agt.profilPosition_ = value as number | "center" | null;
            })
          }
        />
      </React.Fragment>
    </>
  );

  return (
    <ZaunCircleMenuItem
      data-button-key={buttonText}
      onClick={(event) => {
        addDekorProfile();
      }}
    >
      {agt.selectedObjects[0]?.showProfil ? (
        <div className="flex-column" role="group" aria-label={I18N("aria::Profiloptionen")}>
          <div className="flex-row gap-2">
            {(!isRhombusOderRhombusHolz || hatBasisprofile) && HöhenEingabe()}

            <MenuItemSmallButton
              onClick={(event) => {
                event.stopPropagation();
                removeProfile();
                APP.dirty();
                APP.logUserAction({ action: "removeDekorprofileButton" });
              }}
            >
              <SvgButton title={I18N("Löschen")} onClick={() => null} size="large">
                <TrashBinSvg />
              </SvgButton>
              {isRhombusOderRhombusHolz && !hatBasisprofile && (
                <span> &nbsp; {I18N("Profil entfernen")}</span>
              )}
            </MenuItemSmallButton>
          </div>
          {agt.selectedObjects.some((s) => s.hasLichtleisten) && !isLichtleisteMöglich && (
            <MenuItemSmallButton
              onClick={(event) => {
                removeProfile();
                addDekorProfile();
                APP.dirty();
              }}
            >
              {I18N("durch Dekorprofil ersetzen")}
            </MenuItemSmallButton>
          )}
          {agt.selectedObjects.some((s) => s.hasDekorprofil) && isLichtleisteMöglich && (
            <MenuItemSmallButton
              onClick={async () => {
                removeProfile();
                addLichtleiste();
                APP.dirty();
                await Commons.sleep(50);
                APP.trigger("maybeShowLichtleistenOptionen");
              }}
            >
              {I18N("durch Lichtleiste ersetzen")}
            </MenuItemSmallButton>
          )}
        </div>
      ) : (
        buttonText
      )}
    </ZaunCircleMenuItem>
  );
}

export function PositionInput({
  value,
  setter,
  element: elementProp,
  overrideMinPosition,
  overrideMaxPosition,
}: {
  value: number | string;
  setter: (value: number | string) => void;
  element?: ZElement;
  overrideMinPosition?: number;
  overrideMaxPosition?: number;
}) {
  const element = elementProp ?? (APP.arrangement.selectedObjects[0] as ZElement);
  if (!element) return <></>;

  const elementMinMax = element.sonderprofilMinMax;
  const minPosition = overrideMinPosition ?? elementMinMax.minPosition;
  const maxPosition = overrideMaxPosition ?? elementMinMax.maxPosition;
  const schrittweitenOptionen = Array.from({ length: maxPosition }, (_, i) => i + 1).slice(
    minPosition
  );
  schrittweitenOptionen.sort((a, b) => b - a);
  const optionen = schrittweitenOptionen.map(
    (number) =>
      ({
        value: number,
        label: I18N("Pos.") + " " + number,
      }) as { value: number | string; label: string }
  );
  if (element.isSenkrechtprofile)
    optionen.splice(0, 0, { value: "center", label: I18N("zentriert") });
  return <SizeSelect value={value} setter={setter} optionen={optionen} />;
}

interface SizeOption {
  value: number | string;
  label: string;
}

function SizeSelect({
  value,
  setter,
  optionen,
}: {
  value: number | string;
  setter: (value: number | string) => void;
  optionen: SizeOption[];
}) {
  const selected = value == null ? null : _.minBy(optionen, (o) =>
    o.value === value ? -1 : Math.abs(Number(o.value) - Number(value))
  );
  const normalFilter = createFilter<SizeOption>({
    ignoreCase: false,
    ignoreAccents: false,
    trim: true,
    matchFrom: "start",
  });
  const values = optionen.map((o) => o.value);
  const filterCached = cache((value) => {
    const max = _.min(values.filter((o) => o >= value));
    const min = _.max(values.filter((o) => o < value));
    return [min, max].filter((x) => x);
  });
  const epsilon = 0.001;
  const equals = (a, b) => Math.abs(a - b) < epsilon;
  const filter = (candidate, input) => {
    const inputNumber = I18N.parseFloat(input);
    if (equals(candidate.value, inputNumber)) {
      return true;
    }
    return (
      normalFilter(candidate, input) ||
      filterCached(inputNumber).some((n) => equals(n, candidate.value))
    );
  };
  useEffect(() => {
    return () => {
      //unmount
      focusChange(false);
    };
  }, []);

  const focusChange = (focus) => {
    APP.arrangement.highlightProfileActive = focus;
    APP.trigger("changePositionSelectionFocus", APP.arrangement.selectedObjects[0], focus);
  };

  return (
    <React.Fragment>
      <span data-input-key="Profilposition">
        <Select
          classNamePrefix="my-select"
          className="z-index-20"
          value={selected}
          onFocus={() => focusChange(true)}
          onBlur={() => focusChange(false)}
          options={optionen}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            control: (base) => ({ ...base }),
          }}
          onChange={(w) => {
            setter(w?.value);
            APP.dirty();
          }}
          filterOption={filter}
        />
      </span>
    </React.Fragment>
  );
}
